import { router, usePage } from "@inertiajs/react"
import { useCallback, useMemo } from "react"
import { toast } from "sonner"
import {
  EntityScaleSheetModal,
  type EntityScaleSheetHeightUpdate,
  type EntityScaleSheetLoadResult,
  type EntityScaleSheetRow,
} from "@/components/scale-sheet/entity-scale-sheet-modal"
import { useTranslation } from "@/hooks/use-translation"
import { generateCharacterScalePose } from "@/lib/characters-api"
import { characterScalePoseBusy } from "@/lib/character-jobs"
import { fetchMixedScaleSheet } from "@/lib/mixed-scale-sheet-api"
import { propFrontViewBusy } from "@/lib/prop-jobs"
import {
  DEFAULT_PROP_HEIGHT_CM,
  PROP_GRIP_DRAG_MIN_HEIGHT_CM,
  PROP_MIN_HEIGHT_CM,
  patchPropHeights,
} from "@/lib/prop-scale-sheet-api"
import { generatePropFrontView } from "@/lib/props-api"
import { GRIP_DRAG_MIN_HEIGHT_CM, MIN_HEIGHT_CM, patchCharacterHeights } from "@/lib/scale-sheet-api"
import { parseScaleSheetEntityKey } from "@/lib/scale-sheet-layout"
import type { WorkspaceBaseProps } from "@/types/workspace"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  /** Shot-scoped load (workspace shot panel). */
  shotId?: number | null
  /** Project-scoped load (canvas): heroes + sized props from refs. */
  characterIds?: number[]
  propIds?: number[]
  variant?: "project" | "shot"
  nested?: boolean
  onApplied?: () => void
  showExportActions?: boolean
}

function rowsFromMixedResponse(res: Awaited<ReturnType<typeof fetchMixedScaleSheet>>): EntityScaleSheetRow[] {
  const characterRows: EntityScaleSheetRow[] = res.characters.map((c) => ({
    ...c,
    entity_type: "character" as const,
  }))
  const propRows: EntityScaleSheetRow[] = res.props.map((p) => ({
    id: p.id,
    name: p.name,
    saved_height_cm: p.saved_height_cm,
    effective_height_cm: p.effective_height_cm ?? DEFAULT_PROP_HEIGHT_CM,
    scale_pose_url: p.front_view_url,
    image_width: p.image_width,
    image_height: p.image_height,
    entity_type: "prop" as const,
  }))
  return [...characterRows, ...propRows]
}

async function patchMixedHeights(projectId: number, updates: EntityScaleSheetHeightUpdate[]) {
  const characterUpdates = updates
    .filter((u) => u.entity_type === "character")
    .map((u) => ({ character_id: u.id, height_cm: u.height_cm }))
  const propUpdates = updates
    .filter((u) => u.entity_type === "prop")
    .map((u) => ({ prop_id: u.id, height_cm: u.height_cm }))
  if (characterUpdates.length > 0) {
    await patchCharacterHeights(projectId, characterUpdates)
  }
  if (propUpdates.length > 0) {
    await patchPropHeights(projectId, propUpdates)
  }
}

export function MixedScaleSheetModal({
  open,
  onOpenChange,
  projectId,
  shotId = null,
  characterIds,
  propIds,
  variant: variantProp,
  nested = false,
  onApplied,
  showExportActions,
}: Props) {
  const { t } = useTranslation(["shot", "common"])
  const { jobs = [] } = usePage<WorkspaceBaseProps>().props
  const isShot = (variantProp ?? (shotId != null && shotId > 0 ? "shot" : "project")) === "shot"

  const labels = useMemo(
    () => ({
      close: t("common.close"),
      title: t("common.mixedScaleSheetTitle"),
      previewTitle: t("common.mixedScaleSheetTitle"),
      hint: "",
      needTwo: t("common.mixedScaleSheetNeedTwo"),
      applyAll: t("common.mixedScaleSheetApplyAll"),
      heightLabel: t("common.mixedScaleSheetHeightLabel"),
    }),
    [t],
  )

  const regenerateFigureTitle = useCallback(
    (entityKey: string) => {
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed?.entity_type === "prop") return t("common.mixedScaleSheetRegenerateFrontView")
      return t("common.mixedScaleSheetRegenerateScalePose")
    },
    [t],
  )
  const figureGenerateDisabledReason = t("common.mixedScaleSheetScalePoseRequired")

  const minHeightCmForEntity = useCallback((entityKey: string) => {
    const parsed = parseScaleSheetEntityKey(entityKey)
    return parsed?.entity_type === "prop" ? PROP_MIN_HEIGHT_CM : MIN_HEIGHT_CM
  }, [])

  const minDragHeightCmForEntity = useCallback((entityKey: string) => {
    const parsed = parseScaleSheetEntityKey(entityKey)
    return parsed?.entity_type === "prop" ? PROP_GRIP_DRAG_MIN_HEIGHT_CM : GRIP_DRAG_MIN_HEIGHT_CM
  }, [])

  const isFigureBusy = useCallback(
    (entityKey: string) => {
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed?.entity_type === "prop") return propFrontViewBusy(jobs, parsed.id)
      if (parsed?.entity_type === "character") return characterScalePoseBusy(jobs, parsed.id)
      return false
    },
    [jobs],
  )

  const handleRegenerateFigure = useCallback(
    async (entityKey: string) => {
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed == null) return
      try {
        if (parsed.entity_type === "prop") {
          await generatePropFrontView(projectId, parsed.id)
        } else if (parsed.entity_type === "character") {
          await generateCharacterScalePose(projectId, parsed.id)
        } else {
          return
        }
        router.reload({ only: ["jobs"] })
      } catch (e) {
        toast.error(e instanceof Error ? e.message : String(e))
        throw e
      }
    },
    [projectId],
  )

  const loadSheet = useCallback(
    async (
      loadProjectId: number,
      _entityIds: number[] | undefined,
      options: { preview: boolean; shotId?: number; editOnly: boolean },
    ): Promise<EntityScaleSheetLoadResult> => {
      const scopedShotId = options.shotId ?? (shotId != null && shotId > 0 ? shotId : undefined)
      if (scopedShotId != null && scopedShotId > 0) {
        const res = await fetchMixedScaleSheet(loadProjectId, {
          shotId: scopedShotId,
          editOnly: options.editOnly,
        })
        return {
          status: res.status,
          idsKey: res.entity_ids_key,
          image_url: res.image_url,
          image_download_url: res.image_download_url,
          layout_meta: res.layout_meta,
          rows: rowsFromMixedResponse(res),
        }
      }

      const chars = characterIds ?? []
      const propsList = propIds ?? []
      if (chars.length === 0 || propsList.length === 0) {
        return {
          status: "not_applicable",
          idsKey: "",
          image_url: null,
          layout_meta: null,
          rows: [],
        }
      }

      const res = await fetchMixedScaleSheet(loadProjectId, {
        characterIds: chars,
        propIds: propsList,
        editOnly: options.editOnly,
      })
      return {
        status: res.status,
        idsKey: res.entity_ids_key,
        image_url: res.image_url,
        image_download_url: res.image_download_url,
        layout_meta: res.layout_meta,
        rows: rowsFromMixedResponse(res),
      }
    },
    [shotId, characterIds, propIds],
  )

  return (
    <EntityScaleSheetModal
      projectId={projectId}
      open={open}
      onOpenChange={onOpenChange}
      shotId={isShot ? (shotId ?? undefined) : undefined}
      variant={isShot ? "shot" : "project"}
      nested={nested}
      closeOnApply={!isShot}
      onApplied={onApplied}
      showExportActions={showExportActions}
      loadSheet={loadSheet}
      patchHeights={patchMixedHeights}
      labels={labels}
      waitingStatuses={["waiting_pose", "waiting_figure"]}
      exportFilenamePrefix="mixed-scale-sheet"
      defaultHeightCm={DEFAULT_PROP_HEIGHT_CM}
      minHeightCmForEntity={minHeightCmForEntity}
      minDragHeightCmForEntity={minDragHeightCmForEntity}
      minEntities={2}
      figureEntityType="character"
      onRegenerateFigure={handleRegenerateFigure}
      regenerateFigureTitle={regenerateFigureTitle}
      isFigureBusy={isFigureBusy}
      figureGenerateDisabledReason={figureGenerateDisabledReason}
    />
  )
}
